import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wrench, 
  Calendar, 
  FileText, 
  Search, 
  LogOut, 
  User,
  Clock,
  DollarSign,
  ChevronRight,
  Car
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [bookingsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/invoices`, { headers, withCredentials: true })
      ]);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(
        `${API_URL}/api/bookings/${bookingId}/cancel`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      paid: 'bg-green-100 text-green-800 border-green-200'
    };
    return (
      <Badge className={`${statusClasses[status] || 'bg-gray-100'} border font-medium`}>
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                className="hidden sm:flex"
                onClick={() => navigate('/mechanics')}
                data-testid="find-mechanic-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Find Mechanic
              </Button>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-['Barlow'] text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">Manage your bookings and invoices</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border border-slate-200" data-testid="stat-total-bookings">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{bookings.length}</p>
                  <p className="text-sm text-slate-600">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-pending">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length}
                  </p>
                  <p className="text-sm text-slate-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-completed">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                  <Car className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {bookings.filter(b => b.status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200" data-testid="stat-invoices">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{invoices.length}</p>
                  <p className="text-sm text-slate-600">Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <FileText className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
                  <p className="text-slate-600 mb-4">Find a trusted mechanic and book your first service</p>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => navigate('/mechanics')}
                    data-testid="find-mechanic-empty-btn"
                  >
                    Find a Mechanic
                  </Button>
                </CardContent>
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.booking_id} className="bg-white border border-slate-200 card-hover" data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-['Barlow'] text-lg font-bold text-slate-900">
                            {booking.service_type}
                          </h3>
                          {getStatusBadge(booking.status)}
                        </div>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p><span className="font-medium">Mechanic:</span> {booking.mechanic_name}</p>
                          <p><span className="font-medium">Vehicle:</span> {booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}</p>
                          <p><span className="font-medium">Date:</span> {booking.preferred_date} at {booking.preferred_time}</p>
                          {booking.total_amount && (
                            <p><span className="font-medium">Total:</span> ${booking.total_amount.toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <Button 
                            variant="outline" 
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleCancelBooking(booking.booking_id)}
                            data-testid={`cancel-booking-${booking.booking_id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
                  <p className="text-slate-600">Invoices will appear here after your service is complete</p>
                </CardContent>
              </Card>
            ) : (
              invoices.map((invoice) => (
                <Card key={invoice.invoice_id} className="bg-white border border-slate-200 card-hover" data-testid={`invoice-${invoice.invoice_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-['JetBrains_Mono'] text-sm font-medium text-slate-900">
                            #{invoice.invoice_id}
                          </h3>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Labor</p>
                            <p className="font-medium text-slate-900">${invoice.labor_total?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Parts</p>
                            <p className="font-medium text-slate-900">${invoice.parts_total?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Tax</p>
                            <p className="font-medium text-slate-900">${invoice.tax?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Total</p>
                            <p className="font-bold text-orange-600">${invoice.total?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/invoice/${invoice.invoice_id}`)}
                        data-testid={`view-invoice-${invoice.invoice_id}`}
                      >
                        View Details <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CustomerDashboard;
